import {
    updateRoomsInStore,
    updateTagsIndexInStore,
    updateSearchStateInStore,
} from '../actions/calendarRoomActions';
import type { Room } from '../components/RoomSelector/RoomSelector.types';
import type PlacesFilters from 'owa-calendar-scheduling-service/lib/schema/PlacesFilters';
import { searchRooms } from 'owa-calendar-scheduling-service/lib/services/placesService';
import { searchRoomsVNext } from 'owa-calendar-scheduling-service/lib/services/placesVNextService';
import { isFeatureEnabled } from 'owa-feature-flags';
import { isSuccessStatusCode } from 'owa-http-status-codes';
import type { PlacesResponse } from 'owa-location-service';

// Used to make sure only the last request sent is processed
let numRequestsSent = 0;

export function getRoomsFromPlacesApi(roomFilters: PlacesFilters): Promise<Room[]> {
    if (isFeatureEnabled('cal-mf-usePlacesVNext')) {
        return searchRoomsVNext(roomFilters).then(rooms =>
            rooms?.map(room => ({
                name: room.displayName,
                email: room.emailAddress,
                capacity: room.capacity,
                city: room.address?.city,
                bookingType: room.bookingType,
                audioDeviceName: room.audioDeviceName,
                displayDeviceName: room.displayDeviceName,
                videoDeviceName: room.videoDeviceName,
                floor: room.floorNumber,
                isWheelChairAccessible: room.isWheelChairAccessible,
                roomLists: room.roomLists,
                tags: room.tags,
            }))
        );
    } else {
        return searchRooms(roomFilters).then(response => {
            if (!isSuccessStatusCode(response.status)) {
                return Promise.resolve(null);
            }
            return response.json().then((getRoomsResult: PlacesResponse) => {
                if (!getRoomsResult || !getRoomsResult.value) {
                    return Promise.resolve(null);
                }

                return getRoomsResult.value.map(result => ({
                    name: result.Name,
                    email: result.EmailAddress,
                    capacity: result.ResourceDetails?.Capacity,
                    city: result.Address?.City,
                    bookingType: result.ResourceDetails
                        ? result.ResourceDetails.BookingType
                        : 'Unknown',
                    audioDeviceName: result.ResourceDetails?.AudioDeviceName,
                    displayDeviceName: result.ResourceDetails?.DisplayDeviceName,
                    videoDeviceName: result.ResourceDetails?.VideoDeviceName,
                    floor: result.ResourceDetails?.Floor,
                    isWheelChairAccessible: result.ResourceDetails?.IsWheelChairAccessible,
                    roomLists: result.Localities,
                }));
            });
        });
    }
}

export async function updateRoomsFromPlacesApi(roomFilters: PlacesFilters) {
    const thisRequest = ++numRequestsSent;
    updateSearchStateInStore(true);
    let rooms = await getRoomsFromPlacesApi(roomFilters);
    if (thisRequest < numRequestsSent) {
        // Ignore stale responses
        return;
    }
    updateRoomsInStore(rooms);
    updateTagsIndexInStore(rooms);
    updateSearchStateInStore(false);
}
